import type { IdeaStatus } from '@/lib/types'
import { roleLevel, ROLE_LEVEL, HEAD_ROLE, SUPER_DEPARTMENT } from '@/lib/roles'

// MIRROR dari server/src/lib/ideaStatus.ts — wajib dijaga identik.
// Kalau dua berkas ini berbeda, UI menyembunyikan aksi yang sebenarnya boleh
// (atau menampilkan yang akan ditolak server).

// Kolom papan, urut kiri→kanan: Baru → Dikaji → Disetujui → Ditolak → Diterapkan.
// Ditolak duduk tepat di kanan Disetujui karena keduanya hasil dari kajian yang sama;
// Diterapkan tinggal paling kanan sebagai ujung milik pengaju.
// 'pending' tidak punya kolom — ACC/tolaknya lewat /approvals, bukan lewat kartu.
export const BOARD_STATUSES: IdeaStatus[] = ['new', 'reviewing', 'approved', 'rejected', 'implemented']

// Papan terbagi dua wilayah yang tidak saling masuk.
//
// Dua ujung (Baru, Diterapkan) milik PENGAJU: dia yang mengirim idenya dan dia yang
// tahu idenya sudah benar-benar jalan atau belum. Perpindahan persisnya ada di
// SUBMITTER_MOVES di bawah, jadi daftar kolomnya tidak perlu ditulis dua kali.

// Tiga kolom tengah milik HEAD UNIT YANG DITUJU: itu isi pekerjaan mengkaji. Dia tidak
// boleh menarik kartu ke Baru (seolah ide belum pernah masuk) maupun mendorongnya ke
// Diterapkan (mengaku ide orang lain sudah dijalankan).
export const REVIEWER_STATUSES: IdeaStatus[] = ['reviewing', 'approved', 'rejected']

/**
 * Perpindahan yang boleh dilakukan pengaju, dari kolom sekarang ke kolom tujuan.
 *
 * Baru ↔ Diterapkan adalah bolak-balik miliknya sendiri, dan itu KEPUTUSAN PRODUK
 * yang diminta eksplisit: pengaju boleh menandai idenya terlaksana langsung dari Baru
 * tanpa menunggu kajian, karena banyak ide kecil memang dikerjakan sendiri oleh yang
 * mengusulkannya. Konsekuensinya "Diterapkan" tidak berarti ada yang menyetujui.
 *
 * Disetujui → Diterapkan ada di sini karena harus ada: Head berhenti di Disetujui,
 * jadi kalau pengaju tidak bisa menutupnya dari sana, ide yang sudah disetujui tidak
 * akan pernah bisa ditandai terlaksana oleh siapa pun. Dari Dikaji dan Ditolak pengaju
 * tidak bisa apa-apa: kajian yang sedang berjalan dan penolakan bukan miliknya untuk
 * dibatalkan.
 */
export const SUBMITTER_MOVES: Partial<Record<IdeaStatus, IdeaStatus[]>> = {
  new: ['implemented'],
  implemented: ['new'],
  approved: ['implemented'],
}

export function isBoardStatus(s: IdeaStatus): boolean {
  return BOARD_STATUSES.includes(s)
}

/** Bentuk minimum sebuah ide untuk keperluan rute — dipakai server & mirror web. */
export interface IdeaRoute {
  /** Departemen tujuan (bisa lebih dari satu, lintas unit bisnis). */
  targets?: string[] | null
  /** Kolom lama — hanya dipakai kalau `targets` kosong (ide sebelum multi-tujuan). */
  targetScope?: string | null
  targetDepartment?: string | null
}

/** Ide beserta posisinya di papan dan siapa pengajunya. */
export type IdeaOnBoard = IdeaRoute & { status: IdeaStatus; submitterEmail?: string | null }

export interface StatusViewer {
  role?: string | null
  department?: string | null
  email?: string | null
}

/**
 * Departemen tujuan sebuah ide.
 *
 * Ide lama menyimpan tujuannya di dua kolom tunggal (`target_scope`/`target_department`),
 * jadi pembacaannya di-fallback ke sana, BUKAN di-backfill paksa: baris lama tetap utuh.
 * Tanpa tujuan sama sekali → HOLDING, seperti perilaku sebelum rute ada.
 */
export function ideaTargets(idea: IdeaRoute): string[] {
  const list = (idea.targets ?? []).map((d) => String(d).trim()).filter(Boolean)
  if (list.length) return list
  if ((idea.targetScope ?? 'holding') === 'bu' && idea.targetDepartment) return [idea.targetDepartment]
  return [SUPER_DEPARTMENT]
}

/**
 * Ide ini punya rute yang benar-benar DITULIS, bukan hasil cadangan?
 *
 * `ideaTargets` sengaja mengembalikan HOLDING untuk ide tanpa rute, supaya ide lama
 * tetap terbaca. Untuk MENAMPILKAN ide itu sudah cukup, tapi untuk membuka pintu
 * akses tidak: kalau cadangan itu ikut dihitung sebagai alamat, setiap ide lama dan
 * setiap ide tanpa tujuan mendadak "dialamatkan ke HOLDING" dan seluruh Head HOLDING
 * jadi pengkajinya. Pintu akses hanya boleh terbuka untuk alamat yang sungguh ditulis.
 *
 * Cakupannya khusus: dipakai HANYA oleh `canSeeIdea`, jadi yang dijaga adalah pintu
 * MELIHAT. Untuk MENGGESER status, `isIdeaReviewer` masih menghitung cadangan HOLDING
 * sebagai alamat, dan itu memang perilaku lama untuk ide warisan yang tak bertujuan:
 * kalau tidak, ide-ide itu tidak ada yang bisa mengkaji sama sekali.
 *
 * Hari ini penjaga ini pertahanan berlapis, bukan satu-satunya kunci: untuk semua
 * tingkat selain 'director', penonton yang diloloskan cadangan HOLDING sudah lolos
 * `canViewItem` lewat `hasCrossDeptView`, dan tingkat 'director' sudah dihadang
 * pagar brankas. Jadi belum ada tingkat yang bisa dipakai menulis test yang gagal
 * tanpa penjaga ini. Tetap dipasang supaya perubahan aturan di masa depan tidak
 * diam-diam menjadikan cadangan itu alamat sungguhan.
 */
export function hasExplicitRoute(idea: IdeaRoute): boolean {
  if ((idea.targets ?? []).some((t) => String(t).trim())) return true
  return (idea.targetScope ?? '') === 'bu' && !!idea.targetDepartment
}

/**
 * Viewer berhak MENGKAJI ide ini (menggeser kartunya di papan)?
 *
 * Syaratnya satu kalimat: Head ke atas DI UNIT YANG DITUJU. Ide yang dikirim ke HR
 * dikaji Head HR, bukan Head unit sebelah dan bukan Staff HR. Kalau ide ditujukan ke
 * beberapa unit, tiap unit tujuan punya Head-nya masing-masing.
 *
 * Tidak ada pintu belakang di sini, dan itu disengaja: melihat ide lintas unit
 * (hasCrossDeptView) tetap luas, tapi MENGGESER kartu orang lain tidak. Pengawas
 * yang tidak dituju tetap bisa membaca idenya, cuma tidak menyetujuinya diam-diam.
 */
export function isIdeaReviewer(viewer: StatusViewer, idea: IdeaRoute): boolean {
  const dept = (viewer.department ?? '').trim()
  if (!dept) return false
  if (!ideaTargets(idea).includes(dept)) return false
  return roleLevel(viewer.role) >= ROLE_LEVEL[HEAD_ROLE]
}

/** Viewer adalah pembuat ide ini? */
export function isIdeaSubmitter(
  viewer: StatusViewer,
  idea: { submitterEmail?: string | null },
): boolean {
  const email = (viewer.email ?? '').toLowerCase().trim()
  const owner = (idea.submitterEmail ?? '').toLowerCase().trim()
  return !!email && email === owner
}

/**
 * Kolom yang boleh dituju viewer ini dari posisi ide sekarang.
 *
 * Status ide bukan garis satu arah, tapi tiap peran hanya memegang wilayahnya:
 *  - Pengaju bergerak di dua ujung (lihat SUBMITTER_MOVES).
 *  - Head unit yang dituju bergerak bebas di antara tiga kolom kajian, kiri maupun
 *    kanan, dan boleh menariknya dari ujung mana pun. Kajian bisa keliru, jadi salah
 *    taruh harus bisa dibetulkan tanpa bikin ide mati terkunci.
 *  - Ide 'pending' belum masuk papan sama sekali.
 *
 * Pengaju DIPERIKSA DULUAN, dan itu disengaja: orang yang mengirim ide sering juga
 * Head di unit yang dia tuju. Kalau perannya sebagai pengkaji yang menang, dia bisa
 * menyetujui idenya sendiri dan kajiannya jadi stempel kosong. Jadi begitu seseorang
 * tercatat sebagai pengaju, dia terkunci di wilayah pengaju untuk ide itu saja,
 * sementara wewenangnya atas ide orang lain tetap utuh.
 */
export function allowedNextStatusesFor(idea: IdeaOnBoard, viewer: StatusViewer): IdeaStatus[] {
  if (!isBoardStatus(idea.status)) return []
  if (isIdeaSubmitter(viewer, idea)) return SUBMITTER_MOVES[idea.status] ?? []
  if (isIdeaReviewer(viewer, idea)) return REVIEWER_STATUSES.filter((s) => s !== idea.status)
  return []
}

/** Viewer boleh memindahkan ide ini ke `target`? */
export function canMoveStatus(viewer: StatusViewer, idea: IdeaOnBoard, target: IdeaStatus): boolean {
  return allowedNextStatusesFor(idea, viewer).includes(target)
}

/**
 * Alasan penolakan pemindahan status, atau null kalau boleh.
 * Server memakainya sebagai pesan error, web sebagai toast — supaya keduanya
 * menyebut alasan yang sama persis.
 */
export function statusMoveDenial(
  viewer: StatusViewer,
  idea: IdeaOnBoard,
  target: IdeaStatus,
): string | null {
  if (canMoveStatus(viewer, idea, target)) return null
  if (!isBoardStatus(idea.status)) return 'Ide ini masih menunggu ACC, belum masuk papan.'
  if (!isBoardStatus(target)) return 'Kolom tujuan tidak ada di papan ide.'
  if (isIdeaSubmitter(viewer, idea)) {
    return 'Sebagai pengaju, kamu hanya memegang kolom Baru dan Diterapkan. Kajiannya ada di tangan Head unit tujuan.'
  }
  if (isIdeaReviewer(viewer, idea)) {
    return 'Kolom Baru dan Diterapkan milik pengaju ide. Kamu mengkaji di Dikaji, Disetujui, atau Ditolak.'
  }
  return 'Hanya Head di unit yang dituju ide ini yang bisa memindahkan statusnya.'
}
